import React, { useEffect, useRef, useState } from "react";
import { PenTool, Spline, MousePointer2, Trash2, Diamond, X, Loader2, Layers, Pencil, Wand2, ChevronsLeft, ChevronsRight, MoveHorizontal } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";

// Roto shapes: { id, name, mode: "bezier" | "bspline", keys: { [frame]: [{ x, y, ix, iy, ox, oy }] } } — coords 0..1, handles are offsets.
const lerp = (a, b, t) => a + (b - a) * t;
const P = ["x", "y", "ix", "iy", "ox", "oy"];
export const shapeAt = (keys = {}, n) => {
  const fr = Object.keys(keys).map(Number).sort((a, b) => a - b);
  if (!fr.length) return null;
  if (n <= fr[0]) return keys[fr[0]];
  if (n >= fr[fr.length - 1]) return keys[fr[fr.length - 1]];
  const i = fr.findIndex((f) => f > n);
  const a = fr[i - 1], b = fr[i], t = (n - a) / (b - a);
  return keys[a].map((p, j) => Object.fromEntries(P.map((k) => [k, lerp(p[k] || 0, keys[b][j][k] || 0, t)])));
};

// Catmull-Rom style tangents -> Bezier handles, so every point is smooth without dragging.
export const autoSmooth = (pts) => pts.map((p, i) => {
  const a = pts[(i - 1 + pts.length) % pts.length], b = pts[(i + 1) % pts.length];
  const tx = (b.x - a.x) / 6, ty = (b.y - a.y) / 6;
  return { x: p.x, y: p.y, ix: -tx, iy: -ty, ox: tx, oy: ty };
});
const rdp = (pts, eps) => {
  if (pts.length < 3) return pts;
  const [a, b] = [pts[0], pts[pts.length - 1]];
  const dx = b.x - a.x, dy = b.y - a.y, L = Math.hypot(dx, dy) || 1e-9;
  let far = 0, idx = 0;
  pts.forEach((p, i) => { const d = Math.abs(dy * p.x - dx * p.y + b.x * a.y - b.y * a.x) / L; if (d > far) { far = d; idx = i; } });
  return far > eps ? [...rdp(pts.slice(0, idx + 1), eps).slice(0, -1), ...rdp(pts.slice(idx), eps)] : [a, b];
};
// Freehand stroke -> few points with smooth Bezier handles.
export const strokeToShape = (stroke) => {
  if (stroke.length < 3) return null;
  const far = stroke.reduce((b, p, i) => (Math.hypot(p.x - stroke[0].x, p.y - stroke[0].y) > Math.hypot(stroke[b].x - stroke[0].x, stroke[b].y - stroke[0].y) ? i : b), 0);
  const fit = (eps) => [...rdp(stroke.slice(0, far + 1), eps).slice(0, -1), ...rdp(stroke.slice(far), eps)];
  let eps = 0.004, out = fit(eps);
  while (out.length > 40) { eps *= 1.5; out = fit(eps); }
  if (out.length > 3 && Math.hypot(out[0].x - out[out.length - 1].x, out[0].y - out[out.length - 1].y) < 0.02) out = out.slice(0, -1);
  return out.length >= 3 ? autoSmooth(out) : null;
};

const segs = (pts, mode) => pts.map((_, i) => {
  const n = pts.length;
  if (mode === "bspline") {
    const [a, b, c, d] = [0, 1, 2, 3].map((k) => pts[(i + k - 1 + n) % n]);
    return [[(a.x + 4 * b.x + c.x) / 6, (a.y + 4 * b.y + c.y) / 6], [(2 * b.x + c.x) / 3, (2 * b.y + c.y) / 3], [(b.x + 2 * c.x) / 3, (b.y + 2 * c.y) / 3], [(b.x + 4 * c.x + d.x) / 6, (b.y + 4 * c.y + d.y) / 6]];
  }
  const p = pts[i], q = pts[(i + 1) % n];
  return [[p.x, p.y], [p.x + (p.ox || 0), p.y + (p.oy || 0)], [q.x + (q.ix || 0), q.y + (q.iy || 0)], [q.x, q.y]];
});
export const pathOf = (pts, mode, W, H, closed = true) => {
  if (!pts?.length) return "";
  const s = segs(pts, mode).slice(0, closed ? undefined : Math.max(0, pts.length - 1));
  const f = ([x, y]) => `${(x * W).toFixed(1)},${(y * H).toFixed(1)}`;
  if (!s.length) return `M${f([pts[0].x, pts[0].y])}`;
  return `M${f(s[0][0])} ${s.map((g) => `C${f(g[1])} ${f(g[2])} ${f(g[3])}`).join(" ")}${closed ? " Z" : ""}`;
};

const TrackBar = ({ onTrack, tracking }) => (
  <div className="flex items-center gap-1.5 flex-wrap" data-testid="roto-track">
    <span className="text-[10px] font-mono text-zinc-500">Track (move · size · rotation)</span>
    {[["backward", "Backward", ChevronsLeft], ["both", "Both ways", MoveHorizontal], ["forward", "Forward", ChevronsRight]].map(([d, l, I]) => (
      <button key={d} type="button" onClick={() => onTrack(d)} disabled={!!tracking} data-testid={`roto-track-${d}`} className="ax-chip !h-7 text-[11px] disabled:opacity-40">
        {tracking === d ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <I className="w-3.5 h-3.5 mr-1" />}{tracking === d ? "Tracking…" : l}</button>))}
    <span className="text-[10px] text-zinc-500">Follows the object from this frame up to the next shape key or the clip end.</span>
  </div>
);

export const RotoTools = ({ tool, setTool, roto, active, setActive, onChange, n, onApply, applying, hasBase, onSeek, whiteMeans, onTrack, tracking }) => {
  const shapes = roto.shapes || [];
  const sh = shapes.find((s) => s.id === active);
  const keyFrames = sh ? Object.keys(sh.keys).map(Number).sort((a, b) => a - b) : [];
  const setShapes = (f) => onChange({ ...roto, shapes: f(shapes) });
  const delKey = () => setShapes((l) => l.map((s) => { if (s.id !== active || keyFrames.length < 2) return s; const k = { ...s.keys }; delete k[n]; return { ...s, keys: k }; }));
  return (
    <div className="space-y-2" data-testid="roto-tools">
      <div className="flex items-center gap-1.5 flex-wrap">
        {[["draw", "Freehand", Pencil], ["smooth", "Pen · Smooth", Wand2], ["bezier", "Pen · Bezier", PenTool], ["bspline", "Pen · B-Spline", Spline], ["select", "Edit points", MousePointer2]].map(([id, l, I]) => (
          <button key={id} type="button" onClick={() => setTool(id)} data-testid={`roto-tool-${id}`} className={`ax-chip ${tool === id ? "!border-[#1e97f3] text-white" : ""}`}><I className="w-3.5 h-3.5 mr-1" />{l}</button>))}
        <span className="w-px h-5 bg-zinc-800 mx-1" />
        {shapes.map((s) => (
          <button key={s.id} type="button" onClick={() => { setActive(s.id); setTool("select"); }} data-testid={`roto-shape-${s.id}`}
            className={`ax-chip text-[11px] ${s.id === active ? "!border-[#7AF25F] text-white" : ""}`}>{s.name} · {s.mode === "bspline" ? "B-spline" : "Bezier"}</button>))}
        {sh && <button type="button" onClick={() => { setShapes((l) => l.filter((s) => s.id !== active)); setActive(null); }} data-testid="roto-shape-delete" className="ax-chip !text-red-300"><Trash2 className="w-3.5 h-3.5 mr-1" />Delete shape</button>}
      </div>
      {sh && (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="roto-keys">
          <span className="text-[10px] font-mono text-zinc-500">Shape keys</span>
          {keyFrames.map((f) => { const au = (sh.auto || []).includes(f); return <button key={f} type="button" onClick={() => onSeek(f)} data-testid={`roto-key-${f}`} data-auto={au ? "true" : "false"} title={au ? "Tracked key — re-tracking may replace it; edit it to make it manual" : "Manual key"} className={`ax-chip !h-6 text-[10px] ${f === n ? "!border-[#ffd60a] text-white" : ""} ${au ? "opacity-70" : ""}`}><Diamond className={`w-3 h-3 mr-1 ${au ? "text-[#41a7f5]" : "text-[#ffd60a]"}`} />#{f}</button>; })}
          {keyFrames.includes(n) && keyFrames.length > 1 && <button type="button" onClick={delKey} data-testid="roto-key-delete" className="ax-chip !h-6 text-[10px]"><X className="w-3 h-3 mr-1" />Remove key #{n}</button>}
          <span className="text-[10px] text-zinc-500">Move points on any frame to add a key — frames in between are interpolated.</span>
        </div>
      )}
      {sh && onTrack && <TrackBar onTrack={(d) => onTrack(sh, d)} tracking={tracking} />}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 w-56"><span className="text-[10px] font-mono text-zinc-500 shrink-0">Feather {roto.feather || 0}px</span>
          <Slider value={[roto.feather || 0]} min={0} max={100} step={1} onValueChange={([v]) => onChange({ ...roto, feather: v })} data-testid="roto-feather" className="flex-1" /></div>
        <label className="flex items-center gap-1.5 text-[11px] text-zinc-300" data-testid="roto-invert">
          <Switch checked={!!roto.invert} onCheckedChange={(v) => onChange({ ...roto, invert: v })} data-testid="roto-invert-switch" />Invert</label>
        <button type="button" onClick={onApply} disabled={applying || !shapes.length} data-testid="roto-apply"
          className="h-8 px-3 rounded-md text-xs font-semibold bfx-gradient-bg text-white disabled:opacity-40 flex items-center gap-1.5">
          {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Layers className="w-3.5 h-3.5" />}{applying ? "Rendering mask…" : "Use as Luma Mask"}</button>
        <span className="text-[10px] text-zinc-500" data-testid="roto-apply-note">{roto.invert ? "Inside the shapes becomes black, outside white" : "Inside the shapes becomes white"}{whiteMeans ? ` (white = ${whiteMeans})` : ""}{hasBase ? " · joined with your uploaded Luma Mask" : ""}</span>
      </div>
    </div>
  );
};

export const RotoLayer = ({ roto, onChange, n, W, H, tool, active, setActive }) => {
  const svg = useRef(null);
  const drag = useRef(null);
  const [draft, setDraft] = useState(null);
  const [live, setLive] = useState(null);
  const [stroke, setStroke] = useState(null);
  const addShape = (mode, pts) => {
    const id = Math.random().toString(36).slice(2, 8);
    onChange({ ...roto, shapes: [...shapes, { id, name: `Shape ${shapes.length + 1}`, mode, keys: { [n]: pts } }] });
    setActive(id);
  };
  const shapes = roto.shapes || [];
  const k = W / (svg.current?.clientWidth || W);
  useEffect(() => { setDraft(null); }, [tool]);
  useEffect(() => {
    const onKey = (e) => { if (!draft || /INPUT|TEXTAREA|SELECT/.test(e.target.tagName) || e.target.isContentEditable) return; if (e.key === "Escape") setDraft(null); if (e.key === "Backspace") { e.preventDefault(); setDraft((d) => (d.pts.length > 1 ? { ...d, pts: d.pts.slice(0, -1) } : null)); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [draft]);
  const pos = (e) => { const r = svg.current.getBoundingClientRect(); return { x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) }; };
  const commit = (id, pts) => onChange({ ...roto, shapes: shapes.map((s) => (s.id === id ? { ...s, keys: { ...s.keys, [n]: pts }, auto: (s.auto || []).filter((f) => f !== n) } : s)) });

  const down = (e) => {
    if (tool === "select") { setActive(null); return; }
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = pos(e);
    if (tool === "draw") { drag.current = { type: "stroke" }; setStroke([p]); return; }
    const d = draft || { mode: tool, pts: [] };
    const first = d.pts[0];
    if (first && d.pts.length >= 3 && Math.hypot((p.x - first.x) * W, (p.y - first.y) * H) < 12 * k) {
      if (d.mode === "smooth") addShape("bezier", autoSmooth(d.pts)); else addShape(d.mode, d.pts);
      setDraft(null);
      return;
    }
    setDraft({ ...d, pts: [...d.pts, { ...p, ix: 0, iy: 0, ox: 0, oy: 0 }] });
    drag.current = { type: "new", start: p };
  };
  const startEdit = (e, s, pi, type) => {
    if (tool !== "select") return;
    e.stopPropagation();
    svg.current.setPointerCapture(e.pointerId);
    setActive(s.id);
    drag.current = { type, sid: s.id, pi, pts: shapeAt(s.keys, n).map((q) => ({ ...q })) };
    setLive({ sid: s.id, pts: drag.current.pts });
  };
  const move = (e) => {
    const d = drag.current;
    if (!d) return;
    const p = pos(e);
    if (d.type === "stroke") { setStroke((st) => { const l = st[st.length - 1]; return Math.hypot(p.x - l.x, p.y - l.y) > 0.002 ? [...st, p] : st; }); return; }
    if (d.type === "new") {
      if (draft?.mode !== "bezier") return;
      const ox = p.x - d.start.x, oy = p.y - d.start.y;
      setDraft((dr) => dr && { ...dr, pts: dr.pts.map((q, i) => (i === dr.pts.length - 1 ? { ...q, ox, oy, ix: -ox, iy: -oy } : q)) });
      return;
    }
    const pts = d.pts.map((q, i) => {
      if (i !== d.pi) return q;
      if (d.type === "pt") return { ...q, x: p.x, y: p.y };
      const dx = p.x - q.x, dy = p.y - q.y;
      return d.type === "out" ? { ...q, ox: dx, oy: dy, ...(e.altKey ? {} : { ix: -dx, iy: -dy }) } : { ...q, ix: dx, iy: dy, ...(e.altKey ? {} : { ox: -dx, oy: -dy }) };
    });
    d.pts = pts;
    setLive({ sid: d.sid, pts });
  };
  const up = () => {
    const d = drag.current; drag.current = null;
    if (d?.type === "stroke") { const pts = stroke && strokeToShape(stroke); setStroke(null); if (pts) addShape("bezier", pts); return; }
    if (d && d.type !== "new") { commit(d.sid, d.pts); setLive(null); }
  };

  const r = 5 * k;
  return (
    <svg ref={svg} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" onPointerDown={down} onPointerMove={move} onPointerUp={up}
      className={`absolute inset-0 w-full h-full touch-none ${tool === "select" ? "cursor-default" : "cursor-crosshair"}`} data-testid="roto-canvas">
      {shapes.map((s) => {
        const pts = live?.sid === s.id ? live.pts : shapeAt(s.keys, n);
        const on = s.id === active;
        const isKey = s.keys[n] != null;
        return (
          <g key={s.id} data-testid={`roto-shape-path-${s.id}`}>
            <path d={pathOf(pts, s.mode, W, H)} fill="rgba(255,255,255,0.28)" stroke={on ? "#7AF25F" : "#ffd60a"} strokeWidth={2 * k} />
            {on && tool === "select" && pts.map((q, i) => (
              <g key={i}>
                {s.mode === "bezier" && [["in", q.ix, q.iy], ["out", q.ox, q.oy]].map(([t, hx, hy]) => (hx || hy ? (
                  <g key={t}><line x1={q.x * W} y1={q.y * H} x2={(q.x + hx) * W} y2={(q.y + hy) * H} stroke="#41a7f5" strokeWidth={1 * k} />
                    <circle cx={(q.x + hx) * W} cy={(q.y + hy) * H} r={r * 0.8} fill="#41a7f5" onPointerDown={(e) => startEdit(e, s, i, t)} data-testid={`roto-handle-${t}-${i}`} className="cursor-move" /></g>) : null))}
                <rect x={q.x * W - r} y={q.y * H - r} width={r * 2} height={r * 2} fill={isKey ? "#ffd60a" : "#fff"} stroke="#000" strokeWidth={k}
                  onPointerDown={(e) => startEdit(e, s, i, "pt")} data-testid={`roto-point-${i}`} className="cursor-move" />
              </g>))}
          </g>
        );
      })}
      {draft && (
        <g data-testid="roto-draft">
          <path d={draft.mode === "smooth" ? pathOf(autoSmooth(draft.pts), "bezier", W, H, false) : pathOf(draft.pts, draft.mode, W, H, false)} fill="none" stroke="#ff2d55" strokeWidth={2 * k} />
          {draft.pts.map((q, i) => <rect key={i} x={q.x * W - r} y={q.y * H - r} width={r * 2} height={r * 2} fill={i === 0 ? "#ff2d55" : "#fff"} stroke="#000" strokeWidth={k} />)}
        </g>
      )}
      {stroke && <polyline points={stroke.map((q) => `${q.x * W},${q.y * H}`).join(" ")} fill="none" stroke="#ff2d55" strokeWidth={2 * k} data-testid="roto-stroke" />}
    </svg>
  );
};
